import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { APPLICANT_CONTENT } from "@/features/applicant/constants/applicantContent";
import {
  htmlWithoutLastList,
  rentalAcknowledgements,
} from "@/features/auth/utils/inviteAcceptanceContent";

interface RentalVerificationConsentModalProps {
  isOpen: boolean;
  html: string;
  saving: boolean;
  onCancel: () => void;
  onAcknowledge: () => void;
}

const DEFAULT_RENTAL_HTML = `<h2>Rental Verification Form Acknowledgement and Consent</h2>
<p>Please review the following sample acknowledgement and consent before continuing with the rental verification form.</p>
<p>By proceeding, you allow Liberty Rent to contact your current or previous landlord to confirm your rental history, including dates of residence, rent amount, and payment record.</p>
<ul>
<li>I acknowledge that the information I provide may be used to verify my rental history.</li>
<li>I consent to Liberty Rent contacting my current or previous landlord for rental verification.</li>
</ul>
<ul>
<li data-required="1">I have read and understand the Rental Verification Form Acknowledgement and Consent. I acknowledge and consent to the terms outlined above.</li>
</ul>`;

export function RentalVerificationConsentModal({
  isOpen,
  html,
  saving,
  onCancel,
  onAcknowledge,
}: RentalVerificationConsentModalProps) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const copy = APPLICANT_CONTENT.application.sections.rentalVerification;
  const source = html.trim() || DEFAULT_RENTAL_HTML;
  const items = useMemo(
    () => rentalAcknowledgements(source).filter((item) => item.label.trim()),
    [source],
  );
  const bodyHtml = useMemo(() => htmlWithoutLastList(source), [source]);
  const requiredComplete = items
    .filter((item) => item.required)
    .every((item) => checked[item.key]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      data-testid="rental-verification-consent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rental-verification-consent-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <h2
            id="rental-verification-consent-title"
            className="text-lg font-semibold text-gray-900"
          >
            {copy.modalTitle}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
            data-testid="rental-verification-consent-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div
            className="text-sm leading-6 text-gray-700 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_li]:ml-5 [&_li]:list-disc [&_p]:mb-3 [&_ul]:mb-3"
            data-testid="rental-verification-consent-body"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
          <div className="mt-3 space-y-2">
            {items.map((item) => (
              <label
                key={item.key}
                className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 text-[#1597ff] focus:ring-[#1597ff]"
                  checked={Boolean(checked[item.key])}
                  onChange={() =>
                    setChecked((current) => ({
                      ...current,
                      [item.key]: !current[item.key],
                    }))
                  }
                  data-testid={`rental-verification-consent-checkbox-${item.key}`}
                />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            data-testid="rental-verification-consent-cancel"
          >
            {copy.cancel}
          </button>
          <button
            type="button"
            onClick={onAcknowledge}
            disabled={!requiredComplete || saving || items.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-[#1597ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="rental-verification-consent-acknowledge"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {copy.acknowledge}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
